<template>
  <div class="home">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="mb-4">
        <h3 class="mb-0">{{$t('my_space')}}</h3>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Employee Content -->
      <div v-else-if="employee && employee.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: activeTab === 'overview'}" @click="activeTab = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: activeTab === 'holidays'}" @click="activeTab = 'holidays'">{{$t('holidays')}}</a>
          </li>
        </ul>

        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'">
          <div class="row">
            <!-- Left Column: Personal Details -->
            <div class="col-md-8">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('personal_details')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{employee.reference}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{employee.name}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{employee.email || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{employee.phone || '-'}}</strong>
                    </div>
                    <div class="col-md-12 mb-3">
                      <small class="text-muted d-block">{{$t('address')}}</small>
                      <strong>{{employee.address || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('date_of_birth')}}</small>
                      <strong>{{employee.date_of_birth ? formatDate(employee.date_of_birth) : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('gender')}}</small>
                      <strong>{{employee.gender ? $t(employee.gender) : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('nationality')}}</small>
                      <strong>{{employee.nationality || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('national_id')}}</small>
                      <strong>{{employee.national_id || '-'}}</strong>
                    </div>
                    <div class="col-md-12 mb-3">
                      <small class="text-muted d-block">{{$t('emergency_contact')}}</small>
                      <strong>{{employee.emergency_contact_name || '-'}} {{employee.emergency_contact_phone ? '(' + employee.emergency_contact_phone + ')' : ''}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Job Details Card -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-briefcase" class="bi bi-briefcase me-2" />{{$t('job_details')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('position')}}</small>
                      <strong>{{employee.position || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('department')}}</small>
                      <strong>{{employee.department || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('employment_type')}}</small>
                      <strong>{{employee.employment_type ? $t(employee.employment_type) : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('hire_date')}}</small>
                      <strong>{{employee.hire_date ? formatDate(employee.hire_date) : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('work_schedule')}}</small>
                      <strong>{{employee.work_schedule || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column: Quick Info -->
            <div class="col-md-4">
              <!-- Leave Balance Card -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-calendar-check" class="bi bi-calendar-check me-2" />{{$t('leave_balance')}}</h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('annual_leave_balance')}}</small>
                    <h4 class="text-primary mb-0">{{employee.annual_leave_balance || 0}} <small>{{$t('days')}}</small></h4>
                  </div>
                  <div>
                    <small class="text-muted d-block">{{$t('sick_leave_balance')}}</small>
                    <h4 class="text-warning mb-0">{{employee.sick_leave_balance || 0}} <small>{{$t('days')}}</small></h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Holidays Tab -->
        <div v-if="activeTab === 'holidays'">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h5 class="mb-0">{{$t('my_holidays')}}</h5>
                <button @click="openLeaveModal" class="btn btn-primary btn-sm">
                  <AppIcon name="bi-plus-lg" class="bi bi-plus-lg me-1" />{{$t('request_leave')}}
                </button>
              </div>

              <div class="table-responsive">
                <table class="table table-hover table-striped small">
                  <thead>
                    <tr class="text-secondary">
                      <th>{{$t('leave_type')}}</th>
                      <th>{{$t('start_date')}}</th>
                      <th>{{$t('end_date')}}</th>
                      <th>{{$t('days')}}</th>
                      <th>{{$t('reason')}}</th>
                      <th>{{$t('status')}}</th>
                      <th>{{$t('action')}}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="leave in leaves" :key="leave.id">
                      <td>{{$t(leave.leave_type)}}</td>
                      <td>{{formatDate(leave.start_date)}}</td>
                      <td>{{formatDate(leave.end_date)}}</td>
                      <td>{{leave.days_count}}</td>
                      <td>{{leave.reason || '-'}}</td>
                      <td>
                        <span class="badge" :class="{
                          'bg-warning': leave.status === 'pending',
                          'bg-success': leave.status === 'approved',
                          'bg-danger': leave.status === 'rejected',
                          'bg-secondary': leave.status === 'cancelled'
                        }">
                          {{$t(leave.status)}}
                        </span>
                      </td>
                      <td>
                        <button v-if="leave.status === 'pending'" @click="deleteLeave(leave)" class="btn btn-xs btn-danger" style="padding: 5px;font-size: 12px">
                          <AppIcon name="bi-trash" class="bi bi-trash" /> {{$t('cancel')}}
                        </button>
                      </td>
                    </tr>
                    <tr v-if="leaves.length === 0">
                      <td colspan="7" class="text-center text-muted">{{$t('no_leave_requests')}}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Employee Found State -->
      <div v-else-if="!loading" class="text-center py-5">
        <div class="alert alert-warning d-inline-block">
          <AppIcon name="bi-exclamation-triangle" class="bi bi-exclamation-triangle me-2" />
          {{$t('employee_profile_not_found')}}
        </div>
      </div>

      <!-- Request Leave Modal -->
      <div class="modal fade" id="mySpaceLeaveModal" tabindex="-1" aria-labelledby="mySpaceLeaveModalLabel" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="mySpaceLeaveModalLabel">{{$t('request_leave')}}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">{{$t('leave_type')}} <span class="text-danger">*</span></label>
                <select v-model="leaveForm.leave_type" class="form-select">
                  <option value="annual">{{$t('annual_leave')}}</option>
                  <option value="sick">{{$t('sick_leave')}}</option>
                  <option value="unpaid">{{$t('unpaid_leave')}}</option>
                  <option value="maternity">{{$t('maternity_leave')}}</option>
                  <option value="paternity">{{$t('paternity_leave')}}</option>
                  <option value="compassionate">{{$t('compassionate_leave')}}</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label">{{$t('start_date')}} <span class="text-danger">*</span></label>
                <input type="date" v-model="leaveForm.start_date" @change="calculateDays" class="form-control">
              </div>

              <div class="mb-3">
                <label class="form-label">{{$t('end_date')}} <span class="text-danger">*</span></label>
                <input type="date" v-model="leaveForm.end_date" @change="calculateDays" class="form-control">
              </div>

              <div class="mb-3">
                <label class="form-label">{{$t('total_days')}}</label>
                <input type="number" min="0.5" step="0.5" v-model.number="leaveForm.days_count" class="form-control">
                <small class="text-muted">Indiquez les jours de travail effectif; excluez le repos hebdomadaire et les jours fériés payés.</small>
              </div>

              <div class="mb-3">
                <label class="form-label">{{$t('reason')}}</label>
                <textarea v-model="leaveForm.reason" class="form-control" rows="3" :placeholder="$t('enter_reason')"></textarea>
              </div>

              <div class="alert alert-info small">
                <AppIcon name="bi-info-circle" class="bi bi-info-circle" />
                {{$t('annual_leave_balance')}}: <strong>{{employee.annual_leave_balance || 0}}</strong> {{$t('days')}} |
                {{$t('sick_leave_balance')}}: <strong>{{employee.sick_leave_balance || 0}}</strong> {{$t('days')}}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{$t('cancel')}}</button>
              <button type="button" class="btn btn-primary" @click="saveLeave">{{$t('submit_request')}}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '@/helper'

export default {
  name: 'MySpace',
  data() {
    return {
      helper,
      employee: {},
      activeTab: 'overview',
      loading: true,
      leaves: [],
      leaveForm: {
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
        days_count: 0
      },
      leaveModal: null
    }
  },
  async mounted() {
    //check permissions
    if(helper.checkAccess('view_my_space') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_my_space') == -2) return this.$router.push({path : '/subscribe'})

    await this.fetchCurrentEmployee()
    // eslint-disable-next-line no-undef
    this.leaveModal = new bootstrap.Modal(document.getElementById('mySpaceLeaveModal'), {backdrop: 'static', keyboard: false})
  },
  methods: {
    formatDate(date) {
      return helper.formatDate(date)
    },
    formatMoney(amount) {
      return helper.formatMoney(amount)
    },
    async fetchCurrentEmployee() {
      try {
        this.loading = true
        // Fetch current employee using the dedicated endpoint
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employees/current`,{showToast: false})
        this.employee = response.data.employee || {}

        if (this.employee.id) {
          await this.fetchLeaves()
        }

        this.loading = false
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
        this.loading = false
      }
    },
    async fetchLeaves() {
      if (!this.employee.id) return

      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/my/employee-leaves`)
        this.leaves = response.data.data || []
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
      }
    },
    openLeaveModal() {
      this.leaveForm = {
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
        days_count: 0
      }
      this.leaveModal.show()
    },
    calculateDays() {
      if (this.leaveForm.start_date && this.leaveForm.end_date) {
        const start = new Date(this.leaveForm.start_date)
        const end = new Date(this.leaveForm.end_date)
        if (end < start) {
          this.leaveForm.days_count = 0
          return
        }
        const diffTime = end - start
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
        this.leaveForm.days_count = diffDays
      }
    },
    async saveLeave() {
      if (!this.leaveForm.start_date || !this.leaveForm.end_date) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: this.$t('please_fill_required_fields')
        })
        return
      }
      if (!Number(this.leaveForm.days_count)) this.calculateDays()
      if (this.leaveForm.days_count <= 0) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: 'La date de fin doit être postérieure ou égale à la date de début.'
        })
        return
      }

      try {
        const leaveData = {
          ...this.leaveForm
        }

        await helper.customPost(`${process.env.VUE_APP_API}/my/employee-leaves`, leaveData)
        this.leaveModal.hide()
        this.fetchLeaves()
        // Refresh employee data to update balances
        await this.fetchCurrentEmployee()

        window.vueOpener.open({
          message: this.$t('leave_request_submitted_successfully'),
          type: 'success',
          position: 'bottom'
        })
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
      }
    },
    async deleteLeave(leave) {
      const result = await this.$swal.fire({
        title: this.$t('are_you_sure'),
        text: this.$t('cancel_leave_request_confirmation'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_cancel_it')
      })

      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/my/employee-leaves/${leave.id}`)
          this.leaves = helper.withoutDeletedRecords(this.leaves, leave.id)
          // Refresh employee data to update balances
          await this.fetchCurrentEmployee()
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: error.message
          })
        }
      }
    }
  }
}
</script>

<style scoped>
.nav-link {
  cursor: pointer;
}
</style>
